import cn from "classnames";
import { CaseScore } from "../../db/schema";
import { useEffect, useState } from "react";

interface ReportScoreProps {
  score: CaseScore | null | undefined;
  isLoading?: boolean;
}

function ReportScore({ score, isLoading }: ReportScoreProps) {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    if (score?.score !== null && score?.score !== undefined) {
      setAnimatedValue(0);
      const targetScore = score.score;
      const duration = 250;
      const steps = targetScore;
      const interval = duration / steps;

      let current = 0;
      const timer = setInterval(() => {
        current += 1;
        setAnimatedValue(current);

        if (current >= targetScore) {
          clearInterval(timer);
        }
      }, interval);

      return () => clearInterval(timer);
    }
  }, [score?.score]);

  if (isLoading) {
    return null;
  }

  const getScoreLabel = (scoreValue: number | null) => {
    if (scoreValue === null) return "Not Evaluated";
    if (scoreValue >= 90) return "Outstanding";
    if (scoreValue >= 80) return "Excellent";
    if (scoreValue >= 65) return "Satisfactory";
    if (scoreValue >= 30) return "Needs More Detail";
    return "Insufficient Information";
  };

  const getScoreColorClass = (scoreValue: number | null) => {
    if (scoreValue === null) return "border-gray-300 dark:border-base-300";
    if (scoreValue >= 90) return "border-blue-500";
    if (scoreValue >= 80) return "border-green-500";
    if (scoreValue >= 65) return "border-yellow-500";
    if (scoreValue >= 30) return "border-orange-500";
    return "border-red-500";
  };

  const getProgressColorClass = (scoreValue: number | null) => {
    if (scoreValue === null) return "text-gray-300 dark:text-base-content/30";
    if (scoreValue >= 90) return "text-blue-500 dark:text-blue-300";
    if (scoreValue >= 80) return "text-green-500";
    if (scoreValue >= 65) return "text-yellow-500";
    if (scoreValue >= 30) return "text-orange-500";
    return "text-red-500";
  };

  return (
    <div
      data-tour="report-score"
      className={cn("border-2 rounded-lg p-2 flex items-center min-w-0", {
        [getScoreColorClass(score?.score ?? null)]: true,
      })}
    >
      {score !== undefined && score !== null && score.score !== null && (
        <div
          className={cn("radial-progress shrink-0", {
            [getProgressColorClass(score.score)]: true,
          })}
          style={
            {
              "--value": animatedValue,
              "--size": score.score === 100 ? "2.25rem" : "2rem",
              "--thickness": "3px",
            } as React.CSSProperties
          }
          role="progressbar"
        >
          <span className="text-sm font-black">{score.score}</span>
        </div>
      )}
      {/* min-w-0 + leading-tight so a long label ("Unsatisfactory") wraps
          inside a narrow sidebar instead of forcing the card wider than the
          panel — which is what happens at 150% browser zoom. */}
      <h3 className="min-w-0 text-xs 2xl:text-sm font-bold ml-1.5 leading-tight">
        {getScoreLabel(score?.score ?? null)}
      </h3>
    </div>
  );
}

export default ReportScore;
